import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkflowService } from '../../workflow/services/workflowService';
import { DRAFT_VERSION, listedWorkflows, versionsOf } from '../utils/workflowDraft';
import type {
  Workflow,
  WorkflowNode,
  WorkflowSummary,
} from '../../../types/backend';

interface Options {
  isOpen: boolean;
  active: boolean;
  /** Closes the Studio. Routed through the guard below, so a dirty draft is asked about first. */
  onClose: () => void;
}

/** What the Studio shows on the right: a version read-only, the draft open for editing, or a new one. */
export type WorkflowFormMode = 'view' | 'edit' | 'new';

export interface WorkflowVersionRef {
  workflow_id: string;
  version: string;
}

/** What the editor hands back on save. `name` is only read for a new workflow. */
export interface WorkflowDraftSubmission {
  name?: string;
  title: string;
  description: string;
  nodes: WorkflowNode[];
}

export const workflowFormKeys = {
  list: ['workflow-definitions'] as const,
  versions: (workflowId?: string) => ['workflow-definition', workflowId] as const,
  detail: (workflowId?: string, version?: string) =>
    ['workflow-definition', workflowId, version] as const,
};

/**
 * A way out of the editor. The editor holds the working copy of a draft, so every one of these
 * unmounts it and discards whatever is unsaved; each is therefore taken through `exit` below.
 */
type EditorExit =
  | { kind: 'cancel' }
  | { kind: 'show'; ref: WorkflowVersionRef | null; mode: WorkflowFormMode }
  /** Anything outside this hook that unmounts the editor: closing the Studio, leaving the bucket. */
  | { kind: 'run'; act: () => void };

const asMessage = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/**
 * The Studio's workflow form: the list, the version on screen, and every authoring write.
 *
 * Reads go through the query layer (FE-13, ADR-0050). Keying the detail query on the exact
 * version the operator picked keeps two quick clicks honest: the second key is the one rendered,
 * so a slower first response cannot overwrite it.
 *
 * Writes invalidate rather than patch: the backend reconstructs and re-validates the whole
 * workflow on every write, so what it stored is the only copy worth showing. A write's error is
 * the backend's own message, kept verbatim, because a refused publish names the rule it broke
 * and the operator needs that sentence, not a paraphrase.
 */
export const useWorkflowForm = ({ isOpen, active, onClose }: Options) => {
  const service = useWorkflowService();
  const queryClient = useQueryClient();
  const [selectedRef, setSelectedRef] = useState<WorkflowVersionRef | null>(null);
  const [mode, setMode] = useState<WorkflowFormMode>('view');
  // A query's error lives as long as the query does, so dismissing one needs state of its own.
  // Cleared on every new request, so dismissing one failure never hides the next.
  const [dismissed, setDismissed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Whether the editor's working copy differs from what is stored, and the exit waiting on an
  // answer. Nothing navigates while an exit is pending.
  //
  // The editor is the only writer of `dirty`, and reports false as it unmounts, so a draft with
  // unsaved changes always has an editor on screen. Clearing it from here instead would leave the
  // flag lying whenever an exit resolves to the editor already mounted, and the exit after that
  // would pass through unasked. It is also what keeps the question answerable: it is the editor
  // that draws it.
  const [dirty, setDirty] = useState(false);
  const [pendingExit, setPendingExit] = useState<EditorExit | null>(null);

  const enabled = isOpen && active;

  const listQuery = useQuery<WorkflowSummary[]>({
    queryKey: workflowFormKeys.list,
    queryFn: () => service.listWorkflows(true),
    enabled,
  });

  const detailQuery = useQuery<Workflow>({
    queryKey: workflowFormKeys.detail(selectedRef?.workflow_id, selectedRef?.version),
    queryFn: () =>
      service.getWorkflow(selectedRef?.workflow_id as string, selectedRef?.version as string),
    enabled: enabled && selectedRef !== null,
  });

  // The version of the draft this editor is working from, captured when the draft first arrives
  // for a workflow and advanced only by this session's own saves.
  //
  // It cannot be read off the query at save time. The cache is shared and refetches on its own,
  // so another author's save can land underneath an open editor; sending that value satisfies the
  // backend's condition and erases their work, which is the exact failure the condition exists to
  // stop. What the editor is holding on screen is what it was seeded with, and that is what the
  // save has to be judged against.
  //
  // Only the DRAFT row may set it, because `updated_at` belongs to a version row rather than to
  // the workflow. The Studio's ordinary path is to open a workflow on its published version and
  // then click Edit draft, and publishing stamps the published row while leaving the draft's own
  // timestamp alone, so capturing whichever version happened to load first sent the published
  // row's timestamp as the draft's and refused every save by an author working entirely alone.
  // The same-workflow test stays, so a background refetch of the draft still cannot move it.
  const openedAt = useRef<{ workflowId: string; at: string } | null>(null);

  useEffect(() => {
    const loaded = detailQuery.data;
    if (!loaded || loaded.version !== DRAFT_VERSION) return;
    if (openedAt.current?.workflowId !== loaded.workflow_id) {
      openedAt.current = { workflowId: loaded.workflow_id, at: loaded.updated_at };
    }
  }, [detailQuery.data]);

  useEffect(() => {
    if (!isOpen) {
      openedAt.current = null;
      setSelectedRef(null);
      setMode('view');
      setDismissed(false);
      setActionError(null);
      setNotice(null);
      setDirty(false);
      setPendingExit(null);
    }
  }, [isOpen]);

  // Memoised: every mutation below refreshes through it.
  const invalidate = useCallback(
    (workflowId: string) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: workflowFormKeys.list }),
        queryClient.invalidateQueries({ queryKey: workflowFormKeys.versions(workflowId) }),
      ]),
    [queryClient],
  );

  // Memoised: the selection callbacks returned below are props of memo()ed components.
  const show = useCallback((ref: WorkflowVersionRef | null, next: WorkflowFormMode) => {
    setDismissed(false);
    setActionError(null);
    setNotice(null);
    setSelectedRef(ref);
    setMode(next);
  }, []);

  // Memoised: `exit` below depends on it, and the editor holds the callbacks that reach it.
  const leave = useCallback(
    (next: EditorExit) => {
      setPendingExit(null);
      if (next.kind === 'run') {
        next.act();
        return;
      }
      if (next.kind === 'cancel') {
        setActionError(null);
        setMode('view');
        return;
      }
      show(next.ref, next.mode);
    },
    [show],
  );

  // The one gate on leaving the editor: while a draft has unsaved changes the exit is held as a
  // question instead of taken, because the working copy exists only in the mounted editor.
  // Memoised: every exit the Studio offers is built from it, and they are props of the memo()ed
  // editor and list.
  const exit = useCallback(
    (next: EditorExit) => {
      if (mode !== 'view' && dirty) {
        setPendingExit(next);
        return;
      }
      leave(next);
    },
    [mode, dirty, leave],
  );

  const saveMutation = useMutation({
    mutationFn: ({
      draft,
      workflowId,
      openedAt,
    }: {
      draft: WorkflowDraftSubmission;
      workflowId?: string;
      openedAt?: string;
    }) => {
      if (workflowId) {
        // The draft as this editor loaded it, not as it stands on the server. A value read at
        // save time would satisfy the backend's condition while erasing whatever another author
        // saved in between, which is the whole point of sending it.
        if (!openedAt) throw new Error('This draft was not fully loaded; reopen it and try again');
        return service.updateDraft({
          workflow_id: workflowId,
          expected_updated_at: openedAt,
          title: draft.title,
          description: draft.description,
          nodes: draft.nodes,
        });
      }
      if (!draft.name) throw new Error('A new workflow needs a name');
      return service.createWorkflow({
        name: draft.name,
        title: draft.title,
        description: draft.description,
        nodes: draft.nodes,
      });
    },
    onSuccess: (workflow) => invalidate(workflow.workflow_id),
  });

  const publishMutation = useMutation({
    mutationFn: (workflowId: string) => service.publishWorkflow(workflowId),
    onSuccess: (workflow) => invalidate(workflow.workflow_id),
  });

  const deleteMutation = useMutation({
    mutationFn: (workflowId: string) => service.deleteWorkflow(workflowId),
    onSuccess: async (deleted) => {
      // Every version is gone, so nothing keyed on this workflow may refetch it.
      queryClient.removeQueries({ queryKey: workflowFormKeys.versions(deleted.workflow_id) });
      await queryClient.invalidateQueries({ queryKey: workflowFormKeys.list });
    },
  });

  const saveAsync = saveMutation.mutateAsync;
  // Memoised: the editor is memo()ed and takes this as a prop.
  const save = useCallback(
    async (draft: WorkflowDraftSubmission): Promise<boolean> => {
      setActionError(null);
      setNotice(null);
      const workflowId = mode === 'edit' ? selectedRef?.workflow_id : undefined;
      try {
        const saved = await saveAsync({
          draft,
          workflowId,
          openedAt: openedAt.current?.workflowId === workflowId ? openedAt.current?.at : undefined,
        });
        // Our own save moved the row, so the next one is judged against what we just wrote.
        openedAt.current = { workflowId: saved.workflow_id, at: saved.updated_at };
        if (!workflowId) {
          setSelectedRef({ workflow_id: saved.workflow_id, version: DRAFT_VERSION });
          setMode('edit');
        }
        setNotice('Draft saved');
        return true;
      } catch (e) {
        setActionError(asMessage(e, 'The draft was not saved'));
        return false;
      }
    },
    [mode, selectedRef, saveAsync],
  );

  const publishAsync = publishMutation.mutateAsync;
  // Memoised: passed to the memo()ed viewer and editor.
  const publish = useCallback(async (): Promise<boolean> => {
    if (!selectedRef) return false;
    setActionError(null);
    setNotice(null);
    try {
      const published = await publishAsync(selectedRef.workflow_id);
      setSelectedRef({ workflow_id: published.workflow_id, version: published.version });
      setMode('view');
      setNotice(`Published as v${published.version}`);
      return true;
    } catch (e) {
      setActionError(asMessage(e, 'The workflow was not published'));
      return false;
    }
  }, [selectedRef, publishAsync]);

  const deleteAsync = deleteMutation.mutateAsync;
  // Memoised: passed to the memo()ed viewer.
  const deleteWorkflow = useCallback(async (): Promise<boolean> => {
    if (!selectedRef) return false;
    setActionError(null);
    setNotice(null);
    try {
      await deleteAsync(selectedRef.workflow_id);
      setSelectedRef(null);
      setMode('view');
      return true;
    } catch (e) {
      setActionError(asMessage(e, 'The workflow was not deleted'));
      return false;
    }
  }, [selectedRef, deleteAsync]);

  // Memoised: rebuilt only when the listing or the selection changes, not on every keystroke.
  const versions = useMemo(
    () => (selectedRef ? versionsOf(listQuery.data ?? [], selectedRef.workflow_id) : []),
    [listQuery.data, selectedRef],
  );

  // Memoised: rebuilt only when the listing changes, not on every keystroke.
  const summaries = useMemo(() => listedWorkflows(listQuery.data ?? []), [listQuery.data]);

  const error = dismissed
    ? null
    : (listQuery.error && asMessage(listQuery.error, 'Could not load workflows')) ||
      (selectedRef && detailQuery.error
        ? asMessage(detailQuery.error, 'Could not load that workflow')
        : null) ||
      null;

  return {
    summaries,
    selected: selectedRef ? (detailQuery.data ?? null) : null,
    selectedRef,
    versions,
    mode,
    initialLoading: listQuery.isLoading,
    detailLoading: detailQuery.isLoading,
    error,
    // Memoised: a prop of memo()ed Studio components.
    setError: useCallback((next: string | null) => setDismissed(next === null), []),
    actionError,
    // Memoised: a prop of memo()ed Studio components.
    clearActionError: useCallback(() => setActionError(null), []),
    notice,
    saving: saveMutation.isPending,
    publishing: publishMutation.isPending,
    deleting: deleteMutation.isPending,
    // Memoised: a prop of memo()ed Studio components.
    load: useCallback(() => {
      setDismissed(false);
      return queryClient.invalidateQueries({ queryKey: workflowFormKeys.list });
    }, [queryClient]),
    // Memoised: a prop of memo()ed Studio components.
    handleSelect: useCallback(
      (summary: WorkflowSummary) =>
        exit({
          kind: 'show',
          ref: { workflow_id: summary.workflow_id, version: summary.version },
          mode: 'view',
        }),
      [exit],
    ),
    // Memoised: a prop of memo()ed Studio components.
    selectVersion: useCallback(
      (workflowId: string, version: string) =>
        exit({ kind: 'show', ref: { workflow_id: workflowId, version }, mode: 'view' }),
      [exit],
    ),
    // Published versions are immutable, so editing always opens the one mutable row: the DRAFT.
    // Publishing keeps that row, so every workflow has one for as long as it exists.
    editDraft: useCallback(() => {
      if (!selectedRef) return;
      exit({
        kind: 'show',
        ref: { workflow_id: selectedRef.workflow_id, version: DRAFT_VERSION },
        mode: 'edit',
      });
    }, [selectedRef, exit]),
    /**
     * Opens the Studio on one workflow, from outside it. Deliberately not guarded: it runs from an
     * effect, and a callback whose identity changed with the draft's state would re-fire that
     * effect on every keystroke. Nothing is lost by it, because it runs when the Studio opens and
     * no editor is mounted yet.
     */
    editWorkflow: useCallback(
      (workflowId: string) => show({ workflow_id: workflowId, version: DRAFT_VERSION }, 'edit'),
      [show],
    ),
    // Memoised: a prop of memo()ed Studio components.
    startNew: useCallback(() => exit({ kind: 'show', ref: null, mode: 'new' }), [exit]),
    // Memoised: a prop of memo()ed Studio components.
    cancelEdit: useCallback(() => exit({ kind: 'cancel' }), [exit]),
    /**
     * Runs something that would unmount the editor, or asks first when the draft has unsaved
     * changes: closing the Studio, switching bucket, anything the Studio drives from outside.
     */
    // Memoised: the Studio's bucket toggle reads it on every render.
    leaveEditor: useCallback((act: () => void) => exit({ kind: 'run', act }), [exit]),
    /**
     * Closes the Studio, or asks first when the open draft has unsaved changes. Memoised: it is a
     * prop of the memo()ed header and footer.
     */
    closeStudio: useCallback(() => exit({ kind: 'run', act: onClose }), [exit, onClose]),
    /** Reported by the editor: whether its working copy differs from what is stored. */
    setDirty,
    /** True while an exit is waiting on the operator's answer. */
    discardPending: pendingExit !== null,
    // Memoised: props of the memo()ed editor.
    confirmDiscard: useCallback(() => {
      if (pendingExit) leave(pendingExit);
    }, [pendingExit, leave]),
    // Memoised: a prop of the memo()ed editor.
    keepEditing: useCallback(() => setPendingExit(null), []),
    save,
    publish,
    deleteWorkflow,
  };
};
